import React from 'react';

import { Control, Controller } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Grid, Paper, Stack, TextField, Typography } from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import NativeTourCard from './NativeTourCard';
import NativeTourImageField from './NativeTourImageField';
import NativeTourImageList from './NativeTourImageList';
import NativeTourRemoveButton from './NativeTourRemoveButton';
import { MAX_GALLERY_IMAGES, NativeTourForm, VIMEO_ID_PATTERN } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
}

/** The tour's video, the hosts who lead it and the sponsors shown with it. */
export default function NativeTourMediaSection({ control }: Props) {
  return (
    <Stack spacing={3}>
      <NativeTourCard
        title="Gallery"
        description="The tour's images, the same on every option. The first is the hero; up to 30."
      >
        <Controller
          control={control}
          name="gallery"
          render={({ field }) => (
            <NativeTourImageList
              value={field.value}
              onChange={field.onChange}
              max={MAX_GALLERY_IMAGES}
              firstLabel="Hero"
              emptyText="No images yet."
            />
          )}
        />
      </NativeTourCard>

      <NativeTourCard title="Video" description="The Vimeo video at the top of the tour page.">
        <Controller
          control={control}
          name="content.videoId"
          render={({ field }) => {
            const isInvalid = !!field.value && !VIMEO_ID_PATTERN.test(field.value);
            return (
              <TextField
                label="Vimeo video ID"
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value.trim() || null)}
                error={isInvalid}
                helperText={isInvalid ? 'Only the numbers, e.g. 123456789' : 'The number at the end of the Vimeo link'}
                sx={{ maxWidth: 320 }}
                fullWidth
              />
            );
          }}
        />
      </NativeTourCard>

      <NativeTourCard
        title="Hosts"
        description="Who leads the tour. Each host can also be picked for an itinerary day's special call-out."
      >
        <Controller
          control={control}
          name="content.hosts"
          render={({ field }) => {
            const update = (index: number, change: Partial<(typeof field.value)[number]>) =>
              field.onChange(field.value.map((host, i) => (i === index ? { ...host, ...change } : host)));
            return (
              <Stack spacing={2}>
                {field.value.length === 0 && <Typography color="text.secondary">No hosts.</Typography>}
                {field.value.map((host, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <NativeTourImageField
                        label="Photo"
                        value={host.svcImageId || null}
                        onChange={(svcImageId) => update(index, { svcImageId: svcImageId ?? '' })}
                        error={!host.svcImageId}
                        removable={false}
                      />
                      <Stack spacing={2} flex={1}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            label="Name"
                            value={host.name}
                            onChange={(event) => update(index, { name: event.target.value })}
                            error={!host.name.trim()}
                            helperText={!host.name.trim() ? 'Required' : undefined}
                            required
                            fullWidth
                          />
                          <NativeTourRemoveButton
                            label="Remove host"
                            onClick={() => field.onChange(field.value.filter((_, i) => i !== index))}
                          />
                        </Stack>
                        <MarkdownEditor
                          label="About the host"
                          showLabel
                          value={host.description}
                          onChange={(description) => update(index, { description })}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                <Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => field.onChange([...field.value, { svcImageId: '', name: '', description: '' }])}
                  >
                    Add host
                  </Button>
                </Box>
              </Stack>
            );
          }}
        />
        <Box mt={3}>
          <Controller
            control={control}
            name="content.hostsDefaultImage"
            render={({ field }) => (
              <NativeTourImageField
                label="Default host image, shown where a host has no photo"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Box>
      </NativeTourCard>

      <NativeTourCard title="Sponsors" description="Partner logos shown on the tour page.">
        <Controller
          control={control}
          name="content.sponsors"
          render={({ field }) => {
            const update = (index: number, change: Partial<(typeof field.value)[number]>) =>
              field.onChange(field.value.map((sponsor, i) => (i === index ? { ...sponsor, ...change } : sponsor)));
            return (
              <Stack spacing={2}>
                {field.value.length === 0 && <Typography color="text.secondary">No sponsors.</Typography>}
                <Grid container spacing={2}>
                  {field.value.map((sponsor, index) => (
                    <Grid key={index} size={{ xs: 12, md: 6 }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <NativeTourImageField
                            label="Logo"
                            value={sponsor.svcImageId || null}
                            onChange={(svcImageId) => update(index, { svcImageId: svcImageId ?? '' })}
                            error={!sponsor.svcImageId}
                            removable={false}
                          />
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                              label="Name"
                              value={sponsor.name}
                              onChange={(event) => update(index, { name: event.target.value })}
                              error={!sponsor.name.trim()}
                              helperText={!sponsor.name.trim() ? 'Required' : undefined}
                              required
                              fullWidth
                            />
                            <NativeTourRemoveButton
                              label="Remove sponsor"
                              onClick={() => field.onChange(field.value.filter((_, i) => i !== index))}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => field.onChange([...field.value, { svcImageId: '', name: '' }])}
                  >
                    Add sponsor
                  </Button>
                </Box>
              </Stack>
            );
          }}
        />
      </NativeTourCard>
    </Stack>
  );
}
